import { Grid, TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import React from 'react';
import { useIntl } from 'react-intl';
import {
  countryCollectsState,
  stateLabelForCountry,
  statesForCountry
} from '@utils/states';
import messages from './messages';

/*
 * State / province picker, shown only for the countries that collect one
 * (US and Canada — Trello 215).
 *
 * Renders nothing at all for every other country rather than a disabled field:
 * an Irish guide has no state, and an empty greyed-out input reads as something
 * they failed to fill in. Callers can therefore mount this unconditionally.
 *
 * A dropdown rather than free text is the whole point of the card — it is what
 * guarantees the stored value is always a two-letter code, so a state we
 * collected and a state Stripe hands back are the same alphabet.
 */
const StateDropdown = ({ country, value, updateState, error, disabled }) => {
  const intl = useIntl();

  if (!countryCollectsState(country)) {
    return null;
  }

  const states = statesForCountry(country);
  const label = stateLabelForCountry(country);

  return (
    <Grid item xs={12}>
      <Autocomplete
        required
        options={Object.keys(states)}
        // Autocomplete treats `undefined` as uncontrolled and warns the moment a
        // value arrives, which is exactly what happens when the guide picks a
        // country before a state.
        value={value || null}
        disabled={disabled}
        getOptionLabel={option => `${states[option]} (${option})`}
        filterOptions={(options, { inputValue }) => {
          const needle = inputValue.toLowerCase();
          // Match the code as well as the name so someone who knows they're "CA"
          // can type it, rather than being forced to spell California.
          return options.filter(
            option =>
              states[option].toLowerCase().includes(needle) ||
              option.toLowerCase().includes(needle)
          );
        }}
        onChange={updateState}
        renderInput={params => (
          <TextField
            {...params}
            required
            label={label}
            variant="outlined"
            error={error}
            helperText={
              error
                ? intl.formatMessage(
                    label === 'Province'
                      ? messages.provinceError
                      : messages.stateError
                  )
                : ''
            }
          />
        )}
      />
    </Grid>
  );
};

export default StateDropdown;
